import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/imageUtils';
import { toLocalizedNumber, toLocalizedPrice } from '../../utils/localeUtils';
import { usePopup } from '../../context/PopupContext';
import {
  FaPlus, FaSearch, FaTimes, FaTh, FaList,
  FaCheckCircle, FaEdit, FaLeaf, FaSeedling,
  FaTimesCircle, FaArrowRight
} from 'react-icons/fa';
import { FaTrash } from 'react-icons/fa6';
import Pagination from '../../components/Pagination';
import './FarmerProductList.css';

const PER_PAGE = 10;

const FarmerProductList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast, showConfirm } = usePopup();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const u = JSON.parse(userData);
        if (u.role !== 'farmer') { navigate('/'); return; }
        fetchProducts();
      } catch { setLoading(false); }
    } else { navigate('/login'); }
  }, [navigate]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/products/farmer/my-products');
      setProducts((res.data || []).map(p => ({
        _id: p._id,
        name: p.name,
        price: p.price,
        unit: p.unit,
        category: p.category,
        image: p.images?.[0] ? getImageUrl(p.images[0]) : null,
        stock: p.quantity,
        stockStatus: p.quantity === 0 ? 'out' : p.quantity < 10 ? 'low' : 'ok',
        isOrganic: p.isOrganic || false,
        status: p.isAvailable ? 'active' : 'inactive',
        createdAt: p.createdAt
      })));
    } catch { setProducts([]); }
    finally { setLoading(false); }
  };

  const handleDelete = (id) => {
    showConfirm({
      title: t('farmerProductList.deleteProduct'),
      message: t('farmerProductList.deleteConfirm'),
      confirmLabel: t('farmerProductList.deleteProduct'),
      onConfirm: async () => {
        try {
          await api.delete(`/products/${id}`);
          setProducts(prev => prev.filter(p => p._id !== id));
          showToast(t('farmerProductList.productDeleted'), 'success');
        } catch { showToast(t('farmerProductList.deleteFailed'), 'error'); }
      }
    });
  };

  const toggleStatus = async (id) => {
    try {
      const p = products.find(x => x._id === id);
      const next = p.status === 'active' ? 'inactive' : 'active';
      await api.put(`/products/${id}`, { isAvailable: next === 'active' });
      setProducts(prev => prev.map(x => x._id === id ? { ...x, status: next } : x));
    } catch { showToast(t('farmerProductList.updateStatusFailed'), 'error'); }
  };

  useEffect(() => { setPage(1); }, [filterStatus, searchQuery]);

  const filtered = products.filter(p => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
    const matchFilter = filterStatus === 'all'
      || (filterStatus === 'active' && p.status === 'active')
      || (filterStatus === 'low' && p.stockStatus === 'low')
      || (filterStatus === 'out' && p.stockStatus === 'out');
    return matchSearch && matchFilter;
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageProducts = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const counts = {
    all: products.length,
    active: products.filter(p => p.status === 'active').length,
    low: products.filter(p => p.stockStatus === 'low').length,
    out: products.filter(p => p.stockStatus === 'out').length,
  };

  const stockMeta = {
    ok:  { label: 'In Stock',     color: '#22c55e', bg: '#f0fdf4' },
    low: { label: 'Low Stock',    color: '#f59e0b', bg: '#fffbeb' },
    out: { label: 'Out of Stock', color: '#ef4444', bg: '#fef2f2' },
  };

  const filters = [
    { key: 'all',    label: `All (${toLocalizedNumber(counts.all)})` },
    { key: 'active', label: `Active (${toLocalizedNumber(counts.active)})` },
    { key: 'low',    label: `Low Stock (${toLocalizedNumber(counts.low)})` },
    { key: 'out',    label: `Out (${toLocalizedNumber(counts.out)})` },
  ];

  if (loading) {
    return (
      <div className="fpl-page"><Header />
        <div className="fpl-loading"><div className="fpl-dots"><span /><span /><span /></div><p>{t('farmerProductList.loading')}</p></div>
      </div>
    );
  }

  return (
    <div className="fpl-page">
      <Header />
      <div className="fpl-container">

        {/* Header */}
        <div className="fpl-head">
          <div>
            <h1 className="fpl-title">{t('farmerProductList.myProducts')}</h1>
            <p className="fpl-subtitle">{toLocalizedNumber(products.length)} products total</p>
          </div>
          <Link to="/farmer/products/add" className="fpl-add-btn"><FaPlus /> {t('farmerProductList.addNewProduct')}</Link>
        </div>

        {/* Toolbar */}
        <div className="fpl-toolbar">
          <div className="fpl-search">
            <FaSearch className="fpl-search-ico" />
            <input
              type="text"
              placeholder={t('farmerProductList.searchProducts')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && <button className="fpl-search-clear" onClick={() => setSearchQuery('')}><FaTimes /></button>}
          </div>
          <div className="fpl-filters">
            {filters.map(f => (
              <button key={f.key} className={`fpl-pill ${filterStatus === f.key ? 'fpl-pill--on' : ''}`} onClick={() => setFilterStatus(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="fpl-view-toggle">
            <button className={`fpl-vt-btn ${viewMode === 'grid' ? 'fpl-vt-btn--on' : ''}`} onClick={() => setViewMode('grid')}><FaTh /></button>
            <button className={`fpl-vt-btn ${viewMode === 'table' ? 'fpl-vt-btn--on' : ''}`} onClick={() => setViewMode('table')}><FaList /></button>
          </div>
        </div>

        {/* Content */}
        {filtered.length === 0 ? (
          <div className="fpl-empty">
            <FaSeedling className="fpl-empty-icon" />
            <h3>{t('farmerProductList.noProducts')}</h3>
            <p>{t('farmerProductList.tryAdjusting')}</p>
            <Link to="/farmer/products/add" className="fpl-add-btn fpl-add-btn--sm"><FaPlus /> {t('farmerProductList.addFirst')}</Link>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="fpl-grid">
            {pageProducts.map(p => {
              const sm = stockMeta[p.stockStatus] || stockMeta.ok;
              return (
                <div key={p._id} className={`fpl-card ${p.status === 'inactive' ? 'fpl-card--dim' : ''}`}>
                  <Link to={`/products/${p._id}`} className="fpl-card-link" />

                  {/* Image */}
                  <div className="fpl-card-img">
                    {p.image ? (
                      <img src={p.image} alt={p.name} />
                    ) : (
                      <div className="fpl-card-placeholder"><FaLeaf /></div>
                    )}
                    {p.isOrganic && <span className="fpl-organic">Organic</span>}
                    <span className="fpl-stock-tag" style={{ color: sm.color, background: sm.bg }}>{sm.label}</span>

                    {/* Hover actions */}
                    <div className="fpl-card-actions">
                      <Link to={`/farmer/products/edit/${p._id}`} className="fpl-act fpl-act--edit" title="Edit"><FaEdit /></Link>
                      <button className="fpl-act fpl-act--del" onClick={() => handleDelete(p._id)} title="Delete"><FaTrash /></button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="fpl-card-body">
                    <div className="fpl-card-top">
                      <span className="fpl-cat">{p.category}</span>
                      <button
                        className={`fpl-status ${p.status}`}
                        onClick={() => toggleStatus(p._id)}
                      >
                        {p.status === 'active' ? <><FaCheckCircle /> Active</> : <><FaTimesCircle /> Inactive</>}
                      </button>
                    </div>
                    <h3 className="fpl-card-name">{p.name}</h3>
                    <div className="fpl-card-price">Rs. {toLocalizedPrice(p.price)} <span>/ {p.unit}</span></div>

                    {/* Stock bar */}
                    <div className="fpl-bar-wrap">
                      <div className="fpl-bar-track">
                        <div className="fpl-bar-fill" style={{ width: `${Math.min((p.stock / Math.max(p.stock, 100)) * 100, 100)}%`, background: sm.color }} />
                      </div>
                      <span className="fpl-bar-label">{toLocalizedNumber(p.stock)} {p.unit} in stock</span>
                    </div>

                    <div className="fpl-card-foot">
                      <Link to={`/products/${p._id}`} className="fpl-view-btn">View <FaArrowRight /></Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table View */
          <div className="fpl-table-wrap">
            <table className="fpl-table">
              <thead>
                <tr>
                  <th>{t('farmerProductList.product')}</th>
                  <th>{t('farmerProductList.category')}</th>
                  <th>{t('farmerProductList.price')}</th>
                  <th>{t('farmerProductList.stock')}</th>
                  <th>{t('farmerProductList.status')}</th>
                  <th>{t('farmerProductList.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pageProducts.map(p => {
                  const sm = stockMeta[p.stockStatus] || stockMeta.ok;
                  return (
                    <tr key={p._id} className={p.status === 'inactive' ? 'fpl-row--dim' : ''}>
                      <td>
                        <Link to={`/products/${p._id}`} className="fpl-t-product">
                          {p.image ? (
                            <img src={p.image} alt={p.name} className="fpl-t-thumb" />
                          ) : (
                            <div className="fpl-t-ph"><FaLeaf /></div>
                          )}
                          <div>
                            <span className="fpl-t-name">{p.name}</span>
                            {p.isOrganic && <span className="fpl-t-organic">Organic</span>}
                          </div>
                        </Link>
                      </td>
                      <td><span className="fpl-t-cat">{p.category}</span></td>
                      <td className="fpl-t-price">Rs. {toLocalizedPrice(p.price)}/{p.unit}</td>
                      <td>
                        <span className="fpl-t-stock" style={{ color: sm.color, background: sm.bg }}>
                          {toLocalizedNumber(p.stock)} {p.unit}
                        </span>
                      </td>
                      <td>
                        <button className={`fpl-status ${p.status}`} onClick={() => toggleStatus(p._id)}>
                          {p.status === 'active' ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td>
                        <div className="fpl-t-actions">
                          <Link to={`/farmer/products/edit/${p._id}`} className="fpl-t-act fpl-t-act--edit"><FaEdit /></Link>
                          <button className="fpl-t-act fpl-t-act--del" onClick={() => handleDelete(p._id)}><FaTrash /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > 0 && (
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
};

export default FarmerProductList;
